<template>
  <div class="pa-0 w-100">
    <div class="d-flex flex-column align-center handwritten-container">
      <div
        class="w-100"
        :data-test-id="`signature-pad-authorized-${hasDefaultAutograph}`"
      >
        <DeepSignature
          v-model="signatureDataUrl"
          :has-autograph="Boolean(signatureDataUrl)"
          :height="101.88"
        />
      </div>
      <div
        v-if="AuthService.isAuthenticated"
        class="d-flex justify-end align-center w-100 mt-2"
      >
        <button
          class="btn-reset text-body-small text-primary"
          @click="openSignatureEdition"
        >
          {{ i18n.t('texts.customize_signature') }}
        </button>
      </div>
      <div
        v-if="hasRequiredAutograph"
        class="required-signature-card bg-blue-lighten-3 px-4 py-2 my-2 mt-2 rounded border border-primary"
        data-test-id="required-signature-message"
      >
        <v-icon class="mr-1 text-primary" size="28">
          fal fa-circle-info
        </v-icon>
        <span class="ml-4 font-weight-light">
          {{ i18n.t('info.required_signature') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import AuthService from '@/services/authService.ts';
import { useSignStore } from '@/stores/sign.ts';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useEmitter } from '@/composables/useEmitter';
import { useI18n } from 'vue-i18n';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { DeepSignature } from '@deepcloud/deep-ui-lib';

const emitter = useEmitter();
const i18n = useI18n();
const signStore = useSignStore();
const props = defineProps({
  hasDefaultAutograph: { type: Boolean, default: false },
  initialSignatureUrl: {
    type: String,
    default: undefined
  }
});

const { currentDocument } = useDocumentContext();
const signatureDataUrl = ref<string | undefined>(
  props.initialSignatureUrl || undefined
);
let persistPaused = false;

const hasRequiredAutograph = computed(() => {
  return currentDocument.value?.autographRequired && !signatureDataUrl.value;
});

onMounted(() => {
  if (
    signStore.document &&
    !props.initialSignatureUrl &&
    signStore.autographData &&
    AuthService.isAuthenticated
  ) {
    signStore.document.signContext.defaultAutographUrl =
      signStore.autographData;
  }
});

function openSignatureEdition() {
  emitter.$emit('open-signature-edition-dialog');
}

watch(
  () => props.initialSignatureUrl,
  (url) => {
    const next = url || undefined;
    if (next === signatureDataUrl.value) return;
    persistPaused = true;
    signatureDataUrl.value = next;
    nextTick(() => {
      persistPaused = false;
    });
  }
);

watch(signatureDataUrl, async (dataUrl) => {
  if (persistPaused) return;

  if (!dataUrl) {
    if (signStore.document) {
      signStore.document.signContext.defaultAutographUrl = null;
    }
    emitter.$emit('update-autograph-data', null);
    return;
  }

  if (AuthService.isAuthenticated) {
    const previous = signStore.autographData || undefined;
    const success = await signStore.updateAutographImage(dataUrl);
    if (!success) {
      persistPaused = true;
      signatureDataUrl.value = previous;
      await nextTick();
      persistPaused = false;
      return;
    }
    signStore.autographData = dataUrl;
  }

  emitter.$emit('update-autograph-data', dataUrl);
});
</script>

<style lang="scss" scoped>
.handwritten-container {
  padding-top: 16px;
}

.required-signature-card {
  height: auto;
  width: 100%;
  display: flex;
  align-items: center;
}
</style>
